import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { useLocation, useParams } from 'react-router-dom';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert } from 'components/atoms/Alert';
import { Box, Grid, Link, Stack, Typography } from '@mui/material';
import { Elabels, getBreadcrumbsBasic } from '../helpers';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { ERouteLinks } from 'models/route';
import { StyledDetail } from '../styles';
import { Accordion } from 'components/molecules/Accordion';
import PDFViewer from 'components/molecules/PDFViewer';
import { useQuery } from 'hooks/useQuery';
import { useCase } from '../hooks';
import { Loader } from 'components/atoms/Loader';
import { useLang } from 'models/langContext';
import { useDictionary } from 'models/dictionaryContext';
import { findInDictionary } from 'helpers/dictionary';
import { EMIMETypesLabel } from 'constants/MIMETypes';
import DetailsComments from './DetailsComments';
import { isPathEqualToRouteName } from 'utils/route';
import { useAuth } from 'models/authContext';
import { CaseStatus, REVIEW_APPLICATION_CASE_STATUSES } from 'models/cases';

const CaseDetailsReviewCommentPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { getFileReviewData, reviewData, getCaseData, caseData } = useCase();
  const { user, getUser } = useAuth();
  const {
    id = '',
    caseId = '',
    fileId = '',
  } = useParams<{ id: string; caseId: string; fileId: string }>();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;
  const { pathname }: any = useLocation();
  const isFromLE = isPathEqualToRouteName(
    pathname,
    ERouteLinks.LegalEntityCaseDetailsReviewCommentPage,
  );
  const { t } = useTranslations();
  const { clearAlert, samePageAlert } = useAlert();
  const { selectedLanguage } = useLang();
  const { caseProcessTypes, getCaseProcessTypes } = useDictionary();
  const [isAssignedCase, setIsAssignedCase] = useState(false);

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    (async () => {
      await getCaseData(caseId);
    })();
  }, [caseId]);

  useEffect(() => {
    if (user && caseData && caseData?.assignedToUniqueId === user?.userId) {
      setIsAssignedCase(true);
    } else {
      setIsAssignedCase(false);
    }
  }, [caseData]);

  useEffect(() => {
    if (!caseProcessTypes) {
      getCaseProcessTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async function init() {
      setIsLoading(true);
      await getFileReviewData(fileId);
      setIsLoading(false);
    })();
  }, []);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const breadcrubs = getBreadcrumbsBasic(isFromLE, type, t, false, id);

  return (
    <>
      <Breadcrumbs
        items={[
          ...breadcrubs,
          {
            label: t(Elabels.caseDetailsHeader),
            route: isFromLE
              ? `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':caseId', caseId).replace(':id', id)}?type=${type}&expand=cases`
              : `${ERouteLinks.CasesDetailsPage.replace(':caseId', caseId)}`,
          },
          t(Elabels.reviewAndComment),
        ]}
      />
      <StyledPageTitle variant="h4">{t(Elabels.reviewCaseHeader)}</StyledPageTitle>
      {isLoading && <Loader isLoading />}
      {!isLoading &&
        reviewData &&
        caseData?.statusUniqueId &&
        REVIEW_APPLICATION_CASE_STATUSES.includes(caseData.statusUniqueId as CaseStatus) && (
          <Grid
            container
            sx={{ justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}
          >
            <StyledDetail container size={{ xs: 12 }}>
              <Grid size={{ xs: 6, md: 3, xl: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(Elabels.fileName)}
                  </Typography>
                  <Typography variant="body2">{`${reviewData?.filename}.${reviewData.fileType}`}</Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 6, md: 3, xl: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(Elabels.caseID)}
                  </Typography>
                  <Typography variant="body2">
                    <Link
                      underline="none"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={ERouteLinks.CasesDetailsPage.replace(':caseId', caseId)}
                    >
                      {reviewData?.caseNumber}
                    </Link>
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 6, md: 3, xl: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(Elabels.processType)}
                  </Typography>
                  <Typography variant="body2">
                    {findInDictionary(reviewData.processTypeUniqueId, caseProcessTypes)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 6, md: 3, xl: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(Elabels.licenceCodeName)}
                  </Typography>
                  <Typography variant="body2">{`${reviewData?.licenceTemplateCode} ${reviewData?.licenceTemplateName}`}</Typography>
                </Stack>
              </Grid>
            </StyledDetail>
            <Grid
              container
              columnSpacing={2.5}
              sx={{
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                justifyContent: 'space-between',
                rowGap: 2,
                width: '100%',
              }}
            >
              <Grid size={{ xs: 12, md: 7.5 }}>
                <Accordion
                  title={t(Elabels.fileDetails)}
                  name="fileDetails"
                  alwaysExpanded
                  expanded
                >
                  {reviewData.fileType === EMIMETypesLabel.PDF ? (
                    <PDFViewer pdfUrl={reviewData?.presignedUrl} />
                  ) : (
                    <img
                      src={reviewData?.presignedUrl}
                      alt="file"
                      style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
                    />
                  )}
                </Accordion>
              </Grid>
              <Grid size={{ xs: 12, md: 4.5 }}>
                <DetailsComments
                  canManageComments={
                    isAssignedCase && caseData.statusUniqueId === CaseStatus.IN_PROGRESS
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        )}
    </>
  );
};

export default CaseDetailsReviewCommentPage;
